import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import DashboardSidebar from "../components/DashboardSidebar";
import DashboardHeader from "../components/DashboardHeader";
import {
  Search,
  ArrowRight,
  Loader2,
  Clock,
  Timer,
  MessagesSquare,
  HelpCircle,
  Brain,
  Smile,
  ListChecks,
  Sparkles,
  Trophy,
  Lightbulb,
  Mic,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const pickIcon = (title = "") => {
  const t = title.toLowerCase();
  if (t.includes("mark")) return Trophy;
  if (t.includes("buying time") || t.includes("time")) return Timer;
  if (t.includes("don't know") || t.includes("dont know")) return HelpCircle;
  if (t.includes("mind") && t.includes("blank")) return Brain;
  if (t.includes("body language") || t.includes("first impression")) return Smile;
  if (t.includes("structure") || t.includes("structuring")) return ListChecks;
  if (t.includes("difficult") || t.includes("hostile")) return MessagesSquare;
  if (t.includes("golden")) return Sparkles;
  return Lightbulb;
};

const TipCard = ({ tip }) => {
  const navigate = useNavigate();
  const Icon = pickIcon(tip.title);
  const completed =
    typeof window !== "undefined" &&
    localStorage.getItem(`medily.progress.tips.${tip.slug}`) === "1";
  return (
    <div
      onClick={() => navigate(`/interview-tips/${tip.slug}`)}
      onKeyDown={(e) => (e.key === "Enter" ? navigate(`/interview-tips/${tip.slug}`) : null)}
      role="button"
      tabIndex={0}
      className="rounded-2xl bg-white border border-slate-100 overflow-hidden shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition cursor-pointer group flex flex-col"
    >
      <div className="h-32 bg-gradient-to-br from-[#dbe6ff] via-[#e9efff] to-[#f5f8ff] relative overflow-hidden">
        <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full bg-white/40 blur-2xl" />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-16 h-16 rounded-2xl bg-white/80 backdrop-blur border border-white shadow-sm flex items-center justify-center text-[#1d4ed8] group-hover:scale-110 group-hover:rotate-3 transition">
            <Icon className="w-7 h-7" />
          </div>
        </div>
        <div className="absolute top-3 left-3 text-[10px] font-medium tracking-wide px-2 py-0.5 rounded-full bg-white/80 text-[#1d4ed8]">
          <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />{tip.read_minutes} min read</span>
        </div>
        {completed && (
          <div className="absolute top-3 right-3 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-[#15803d] text-white inline-flex items-center gap-1">
            ✓ Completed
          </div>
        )}
      </div>
      <div className="p-4 flex flex-col flex-1">
        <p className="font-semibold text-slate-900 text-[15px] group-hover:text-[#1d4ed8] leading-tight">{tip.title}</p>
        <p className="text-[12.5px] text-slate-500 mt-1.5 line-clamp-2 leading-relaxed flex-1">
          {tip.description || "Read this tip."}
        </p>
        <span className="mt-3 text-[13px] font-medium text-[#1d4ed8] inline-flex items-center gap-1 group-hover:gap-2 transition-all">
          Continue Reading <ArrowRight className="w-3.5 h-3.5" />
        </span>
      </div>
    </div>
  );
};

const InterviewTips = () => {
  const [tips, setTips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/tips`);
        if (!mounted) return;
        setTips(res.data.tips || []);
      } catch {
        setError("Could not load interview tips.");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return tips;
    return tips.filter(
      (t) =>
        t.title.toLowerCase().includes(needle) ||
        (t.description || "").toLowerCase().includes(needle)
    );
  }, [q, tips]);

  return (
    <div className="min-h-screen bg-[#f5f8fd] flex">
      <DashboardSidebar />
      <div className="flex-1 min-w-0">
        <DashboardHeader />
        <main className="p-6 lg:p-10">
          <section className="rounded-3xl bg-white border border-slate-100 shadow-sm p-6 md:p-10 relative overflow-hidden">
            <div className="absolute -top-24 -right-24 w-[420px] h-[420px] bg-[#dbe6ff] rounded-full blur-3xl opacity-60 -z-0" />

            <div className="relative flex items-start justify-between flex-wrap gap-6">
              <div>
                <span className="inline-block px-3 py-1 rounded-full bg-[#eef4ff] text-[#1d4ed8] text-[11px] font-semibold tracking-wide uppercase">
                  Interview Tips
                </span>
                <h1 className="mt-3 text-[30px] md:text-[36px] font-semibold text-slate-900 tracking-tight leading-tight">
                  Your Interview Tips
                </h1>
                <p className="mt-2 text-slate-500 max-w-2xl text-[14.5px]">
                  Master the skills that separate average applicants from outstanding candidates with expert interview strategies, communication techniques and practical advice for every MMI station.
                </p>
              </div>
              <div className="relative w-full max-w-sm">
                <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search all tips..."
                  className="w-full pl-10 pr-4 py-3 rounded-full border border-slate-200 text-sm bg-white/90 focus:outline-none focus:border-[#1d4ed8] focus:ring-2 focus:ring-[#1d4ed8]/10"
                />
              </div>
            </div>

            <div className="relative mt-8">
              {loading && (
                <div className="flex items-center justify-center py-16 text-slate-400">
                  <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading tips…
                </div>
              )}
              {error && !loading && (
                <div className="text-center text-red-500 py-10">{error}</div>
              )}
              {!loading && !error && (
                <>
                  {q.trim() && (
                    <div className="mb-4 text-[13.5px] text-slate-500">
                      <span className="font-medium text-slate-700">{filtered.length}</span> result{filtered.length === 1 ? "" : "s"} across your interview tips
                    </div>
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
                    {filtered.map((t) => (
                      <TipCard key={t.slug} tip={t} />
                    ))}
                    {filtered.length === 0 && (
                      <div className="col-span-full text-center text-slate-400 py-10">No tips match your search.</div>
                    )}
                  </div>
                </>
              )}
            </div>
          </section>
        </main>
      </div>
    </div>
  );
};

export default InterviewTips;
